import { useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import { integrationSystemDisplayName, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import {
  useGetAppointmentSettingsQuery,
  ProviderFragment,
  RoomFragment,
  AppointmentTypeFragment,
} from 'shared/types/graphql';

export const useAppointmentSettings = (): {
  apptTypes: AppointmentTypeFragment[];
  showAppointmentsInPetPortal: boolean;
  areAppointmentSettingsLoading: boolean;
  providers: ProviderFragment[];
  rooms: RoomFragment[];
} => {
  const { currentClinic } = useClinicUser();
  const { primaryIntegrationName } = useIntegrationsProvider();

  const { data: appointmentSettingsData, loading: areAppointmentSettingsLoading } = useGetAppointmentSettingsQuery({
    variables: {
      clinicId: currentClinic?.id || '',
      clinicSettingId: currentClinic?.clinicSetting?.id || '',
    },
    skip: !currentClinic?.id || !currentClinic?.clinicSetting?.id,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const showAppointmentsInPetPortal = useMemo(
    () => !!appointmentSettingsData?.findUniquePetPortalSetting?.showAppointments,
    [appointmentSettingsData?.findUniquePetPortalSetting?.showAppointments],
  );

  const apptTypes = useMemo(() => appointmentSettingsData?.findManyAppointmentType ?? [], [appointmentSettingsData]);

  const providers = useMemo(
    () => appointmentSettingsData?.findManyClinicEmployee ?? [],
    [appointmentSettingsData?.findManyClinicEmployee],
  );

  /**
   * Avimark clinic rooms are all of type Facility, so only apply
   * the type filter to Cornerstone
   */
  const filteredRooms = useMemo(() => {
    const rooms = appointmentSettingsData?.findManyClinicRoom ?? [];
    if (primaryIntegrationName === integrationSystemDisplayName.CORNERSTONE) {
      return rooms.filter((room) => room.type?.toLowerCase() === 'room');
    }
    return rooms;
  }, [appointmentSettingsData?.findManyClinicRoom, primaryIntegrationName]);

  return {
    apptTypes,
    showAppointmentsInPetPortal,
    areAppointmentSettingsLoading,
    providers,
    rooms: filteredRooms,
  };
};
